import { stageText } from "@/lib/manual-call";
import type { DialDetection, DialerCall, DialerOffer, OfferedLead } from "@/lib/types";

/**
 * The dialer on the rep's phone (ADR-019): the words and the arithmetic of a preview offer, and when
 * a voicemail drop applies. Pure, for `test/dialer.test.ts`.
 */

/** Whole seconds until the offer goes back to the pool — rounded up, so "0" means it has ended —
 *  or null when it has no deadline the console can read. */
export function secondsLeft(expiresAt: string | null, now: number): number | null {
  if (!expiresAt) return null;
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

/**
 * How often the lead has been called, and how the last call went: "First call", "Called once
 * before · No answer". `label` turns the last outcome's slug into the campaign's word for it; a slug
 * it does not know is shown as the server spelled it.
 */
export function attemptsText(
  offer: Pick<DialerOffer, "attempts" | "last_disposition">,
  label: (slug: string) => string | undefined,
): string {
  const { attempts, last_disposition: last } = offer;
  const times =
    attempts === 0
      ? "First call"
      : attempts === 1
        ? "Called once before"
        : `Called ${attempts} times before`;
  if (attempts === 0 || !last) return times;
  return `${times} · ${label(last) ?? last.replaceAll("_", " ")}`;
}

/**
 * The list's other columns worth a glance before dialing, in the file's order and at most `max` of
 * them. A value that is empty, or not text or a number, is left out rather than printed as
 * `[object Object]`.
 */
export function leadDetails(
  attributes: Record<string, unknown> | null,
  max = 6,
): [label: string, value: string][] {
  const rows: [string, string][] = [];
  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (rows.length === max) break;
    if (typeof value !== "string" && typeof value !== "number") continue;
    const text = String(value).trim();
    if (text) rows.push([key.replaceAll("_", " "), text]);
  }
  return rows;
}

/** Where the lead is, for the clock that decides when they may be called: "CA · America/Los_Angeles · ES". */
export function leadPlace(lead: Pick<OfferedLead, "state" | "timezone" | "language">): string | null {
  const parts = [lead.state, lead.timezone, lead.language?.toUpperCase()].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** How long the phone keeps naming a lead it dialed: well past any dial timeout and ring. */
export const DIALED_LEAD_MS = 2 * 60_000;

/**
 * The lead the rep dialed from an offer, while that dial is recent enough to be the call the dialer
 * is placing. The offer has left the server by then. An old one must not name the next lead — in
 * power mode, say, which dials without offering anything.
 */
export function recentDialedLead(
  dialed: { offer: DialerOffer; at: number } | null,
  now: number,
): DialerOffer | null {
  return dialed !== null && now - dialed.at < DIALED_LEAD_MS ? dialed.offer : null;
}

/**
 * The library with the lead's own message first (ADR-061).
 *
 * core-api chooses it from the lead's stage and language; the phone only has to lead with it. The
 * rest of the library follows in its own order and is one click away, because a rep who has just
 * learned something on the call knows more than the stage does.
 */
export function voicemailOrder<T extends { slug: string }>(
  messages: readonly T[],
  suggested: string | null,
): T[] {
  if (!suggested) return [...messages];
  const first = messages.filter((m) => m.slug === suggested);
  return [...first, ...messages.filter((m) => m.slug !== suggested)];
}

/** A message can be left only on a connected call to a lead the dialer reached. */
export function canDropVoicemail(call: { state: string; kind: string | null } | null): boolean {
  return call !== null && call.state === "busy" && call.kind === "outbound";
}

/* --- what the dialer does with a lead's answer (ADR-104 in `platform`) ------------------------- */

/**
 * What an answer means on the dial the phone is showing.
 *
 * With detection off, or passive, an answered lead is brought to the rep at once. A campaign whose
 * detection decides for itself listens first: at the answer nobody is being brought to anybody, the
 * rep is rung only for a person, and a machine is hung up on. The phone said "bringing them to
 * your phone" for all of it, which was wrong for every voicemail.
 *
 * Two machines are not hung up on, both on an attempt the cadence set to leave a message. Where
 * a rep leaves it, the dialer brings that voicemail to them (`message`). Where the dialer leaves
 * it by itself (ADR-109 in `platform`), nobody is rung: the rep is let go and the message plays
 * after the cadence's wait (`leaving`).
 */
export type AnswerState = "bringing" | "listening" | "voicemail" | "message" | "leaving" | "person";

/** Whether a voicemail on this dial comes to the rep, to leave the cadence's message. */
function leavesMessage(detection: DialDetection | null | undefined): boolean {
  return detection?.on_voicemail === "leave_message";
}

/** Whether the dialer leaves the cadence's message on this dial by itself. */
function dialerLeaves(detection: DialDetection | null | undefined): boolean {
  return detection?.on_voicemail === "leave_message_auto";
}

export function answerState(detection: DialDetection | null | undefined): AnswerState {
  if (!detection || detection.mode === "off" || detection.passive) return "bringing";
  if (detection.result === null) return "listening";
  if (detection.result !== "machine") return "person";
  if (dialerLeaves(detection)) return "leaving";
  return leavesMessage(detection) ? "message" : "voicemail";
}

/** The line that says how far the dial has got. */
export function dialStageText(
  stage: DialerCall["stage"] | "requested",
  detection: DialDetection | null | undefined,
): string {
  if (stage !== "answered") return stageText(stage);
  switch (answerState(detection)) {
    case "listening":
      return "They answered. Checking for a voicemail…";
    case "voicemail":
      return "Voicemail detected. Hanging up…";
    case "message":
      return "Voicemail detected. Bringing it to your phone…";
    case "leaving":
      return "Voicemail detected. The dialer is leaving the message.";
    case "person":
      return "A person answered. Bringing them to your phone…";
    case "bringing":
      return stageText(stage);
  }
}

/**
 * The line under it: what the rep is waiting for, or null when the stage has said it all.
 * `messageSaid` is the card saying beside it which message a voicemail gets on this attempt
 * (`attemptVoicemailText`): the line then leaves that half to it.
 */
export function dialWaitText(
  stage: DialerCall["stage"] | "requested",
  detection: DialDetection | null | undefined,
  messageSaid = false,
): string | null {
  const answer = answerState(detection);
  if (answer === "bringing") {
    return stage === "answered" ? null : "Your phone rings the moment they answer.";
  }
  if (answer === "message") return "The cadence leaves a message on this call.";
  if (answer === "leaving") return "Your phone does not ring for this one.";
  if (stage === "answered" && answer !== "listening") return null;
  if (leavesMessage(detection)) {
    return "Your phone rings for a person, and for a voicemail, to leave the message.";
  }
  if (dialerLeaves(detection) && !messageSaid) {
    return "Your phone rings only for a person. On a voicemail the dialer leaves the message.";
  }
  return stage === "answered"
    ? "Your phone rings only if it is a person."
    : "Your phone rings once the dialer has heard a person.";
}

/** One step of a dial on the Phone page's stage. `stopped` is the step a dial ended on. */
export interface DialStep {
  label: string;
  state: "done" | "current" | "todo" | "stopped";
}

/**
 * How far a dial has got, as the steps drawn across the Phone page's stage: called, ringing,
 * answered, and the rep's phone. A campaign whose detection decides for itself has one more
 * between the answer and the phone, the dialer checking for a voicemail, and a dial that ends
 * there says so in that step, whether the dialer hung up or is leaving the message by itself. A
 * voicemail that comes to the rep, to leave the cadence's message, says so in that step too and
 * goes on to the phone.
 */
export function dialSteps(
  stage: DialerCall["stage"] | "requested",
  detection: DialDetection | null | undefined,
): DialStep[] {
  const answer = answerState(detection);
  const listens = answer !== "bringing";
  const labels = [
    "Calling",
    "Ringing",
    "Answered",
    ...(listens ? ["Voicemail check"] : []),
    "Your phone",
  ];
  const voicemail = stage === "answered" && (answer === "voicemail" || answer === "leaving");
  const message = stage === "answered" && answer === "message";
  const at =
    stage === "requested" || stage === "dialing"
      ? 0
      : stage === "ringing"
        ? 1
        : answer === "person" || answer === "message"
          ? 4
          : 3;
  return labels.map((label, i) => ({
    label: (voicemail && i === at) || (message && i === 3) ? "Voicemail detected" : label,
    state: i < at ? "done" : i > at ? "todo" : voicemail ? "stopped" : "current",
  }));
}

/** A dial that ended for the rep on a voicemail, hung up on or left to the dialer's own message:
 *  which, whose, and when the phone learned it. */
export interface HeardVoicemail {
  callId: string;
  leadId: string;
  name: string;
  at: number;
  /** The dialer is leaving the cadence's message on it by itself (ADR-109 in `platform`). */
  leaving?: boolean;
}

/** How long the phone keeps saying so. The dial itself is gone within a moment of the verdict,
 *  and in power mode the next one is already on screen. */
export const VOICEMAIL_NOTE_MS = 5_000;

/**
 * What the phone says about a dial that ended on a detected voicemail, once that dial has left
 * the screen: nothing after `VOICEMAIL_NOTE_MS`, and nothing while the dial is still shown (its
 * own line says it). It names the lead when another lead is being called by then.
 */
export function voicemailNote(
  heard: HeardVoicemail | null,
  current: { call_id: string; lead: { id: string } } | null,
  now: number,
): string | null {
  if (heard === null || now - heard.at >= VOICEMAIL_NOTE_MS) return null;
  const did = heard.leaving ? "is leaving the message" : "hung up";
  if (current === null) return `Voicemail detected. The dialer ${did}.`;
  if (current.call_id === heard.callId) return null;
  // No redial follows a message, so the same lead again is a hang-up and its redial.
  return current.lead.id === heard.leadId && !heard.leaving
    ? "Voicemail detected on the last call. The dialer hung up and is calling them again."
    : `${heard.name}: voicemail detected. The dialer ${did}.`;
}

/* --- a call's redials on the dialing card (ADR-105 in `platform`) ------------------------------ */

/**
 * A cadence call can carry redials: when nobody answers, the dialer calls the same lead again at
 * once, for the same rep, a set number of times. The dialing card draws the run as a row of
 * attempts (the call, then each redial), says which one is ringing, from which number, and what a
 * voicemail gets on it, and lets the rep stop the run.
 */

/** What the dialing card reads of the dial for all of it. */
type DialedAttempt = Pick<
  DialerCall,
  "redial" | "step_n" | "caller_id" | "earlier" | "on_voicemail" | "message_to_leave"
>;

/** One attempt of the call being dialed, as a tile on the card. */
export interface AttemptTile {
  /** "Call 1", "Redial 2". */
  label: string;
  state: "done" | "current" | "todo";
  /** How it went, how far it has got, or when it would be made. */
  text: string;
}

/** An attempt by its name: the call by its place in the round, a redial by its number. */
export function attemptName(step: number | null | undefined, n: number): string {
  return n === 0 ? `Call ${step ?? 1}` : `Redial ${n}`;
}

/** How the switch ends a dial nobody talked on, in a word or two. Anything else is shown as the
 *  server spelled it. */
const DIAL_OUTCOMES: Record<string, string> = {
  no_answer: "No answer",
  busy: "Busy",
  voicemail: "Voicemail",
  failed: "Did not connect",
  canceled: "Stopped",
  abandoned: "Dropped",
  human_contact: "Answered",
};

/** How an attempt that is over went: the outcome recorded for it, in the campaign's word, else
 *  how the switch said it ended. */
function attemptOutcomeText(
  attempt: { outcome: string | null; disposition: string | null } | undefined,
  label: (slug: string) => string | undefined,
): string {
  if (!attempt) return "Made";
  if (attempt.disposition) {
    return label(attempt.disposition) ?? attempt.disposition.replaceAll("_", " ");
  }
  if (!attempt.outcome) return "Made";
  return DIAL_OUTCOMES[attempt.outcome] ?? attempt.outcome.replaceAll("_", " ");
}

/**
 * The call's attempts as tiles, in order: the ones made with how each went, the one being made
 * with how far it has got, and the ones that follow if nobody answers. Null on a call that
 * carries no redials, where there is one attempt and nothing to draw. `voicemail` is the dial on
 * screen having reached a machine, which the tile then says instead of "Answered".
 */
export function attemptTiles(
  call: DialedAttempt | null | undefined,
  stage: DialerCall["stage"] | "requested",
  voicemail: boolean,
  label: (slug: string) => string | undefined,
): AttemptTile[] | null {
  const redial = call?.redial;
  if (!call || !redial) return null;
  const of = Math.max(redial.of, redial.n);
  const now = stage === "answered" ? (voicemail ? "Voicemail" : "Answered") : stage === "ringing" ? "Ringing" : "Calling";
  return Array.from({ length: of + 1 }, (_, n) => ({
    label: attemptName(call.step_n, n),
    state: n < redial.n ? "done" : n === redial.n ? "current" : "todo",
    text:
      n < redial.n
        ? attemptOutcomeText(call.earlier?.find((a) => a.n === n), label)
        : n === redial.n
          ? now
          : "If no answer",
  }));
}

/** The card's title while a redial is on its way: "Redialing, 1 of 2". Null on the call itself,
 *  and from the answer on, when what became of the answer is the news. */
export function redialTitle(
  call: DialedAttempt | null | undefined,
  stage: DialerCall["stage"] | "requested",
): string | null {
  const redial = call?.redial;
  if (!redial || redial.n < 1 || stage === "answered") return null;
  return `Redialing, ${redial.n} of ${Math.max(redial.of, redial.n)}`;
}

/** Why a redial is being made, by how the attempt before it ended. */
function whyRedialed(step: number | null | undefined, n: number, outcome: string | null): string {
  const before = attemptName(step, n - 1);
  if (outcome === "no_answer") return `Nobody answered ${before.toLowerCase()}.`;
  if (outcome === "busy") return `${before} was busy.`;
  if (outcome === "voicemail") return `${before} reached a voicemail.`;
  return `${before} did not connect.`;
}

/** The number an attempt presents, by how the cadence came to it. A fallback is recorded as
 *  `unbranded->branded`: what the call got is the part after the arrow. */
function fromNumber(strategy: string | null, number: string): string {
  switch (strategy?.split("->").pop()) {
    case "branded":
      return `From the branded number, ${number}.`;
    case "local":
      return `From a local number, ${number}.`;
    case "unbranded":
      return `From another of our numbers, ${number}.`;
    case "rotate":
      return `From our next number in turn, ${number}.`;
    case "same_as_last":
      return `From the number of their last call, ${number}.`;
    default:
      return `From ${number}.`;
  }
}

/**
 * The line under the title before the lead answers: for a redial why it is made, and for every
 * attempt of a cadence call the number the lead sees. "Nobody answered call 1. Same number,
 * (310) 555-0175." Null on a dial that is no cadence call, and where core-api does not say the
 * number. `format` prints a number the way the console does.
 */
export function attemptFromText(
  call: DialedAttempt | null | undefined,
  format: (number: string) => string,
): string | null {
  if (!call || call.step_n === null || call.step_n === undefined) return null;
  const n = call.redial?.n ?? 0;
  const before = n > 0 ? call.earlier?.find((a) => a.n === n - 1) : undefined;
  const why = before ? whyRedialed(call.step_n, n, before.outcome) : null;
  const id = call.caller_id;
  if (!id) return why;
  const from =
    before && before.number === id.number
      ? `Same number, ${format(id.number)}.`
      : fromNumber(id.strategy, format(id.number));
  return why ? `${why} ${from}` : from;
}

/**
 * What a voicemail gets on this attempt, said before anybody answers: the message the cadence
 * leaves, by its name, and who leaves it. The dialer leaves it by itself where the cadence says
 * so and the campaign's detection decides for itself (ADR-109); everywhere else the voicemail
 * reaches the rep, who leaves it with one click. Null on an attempt that hangs up, and when the
 * library has nothing to leave.
 */
export function attemptVoicemailText(
  call: DialedAttempt | null | undefined,
  detection: DialDetection | null | undefined,
): string | null {
  const message = call?.message_to_leave;
  if (!call || !message) return null;
  if (call.on_voicemail === "leave_message_auto" && answerState(detection) !== "bringing") {
    return `If a voicemail answers, the dialer leaves “${message.name}” by itself.`;
  }
  const which = (call.redial?.n ?? 0) > 0 ? "this redial" : "this call";
  return `If a voicemail answers, ${which} leaves “${message.name}”.`;
}

/** Whether the card offers Stop redialing: on a redial nobody has answered yet. Stopping hangs
 *  it up, makes no more for this call, and the rep gets their next lead. */
export function canStopRedials(
  call: DialedAttempt | null | undefined,
  stage: DialerCall["stage"] | "requested",
): boolean {
  return (call?.redial?.n ?? 0) >= 1 && stage !== "answered";
}

/* --- the dial as one strip on the Phone page ---------------------------------------------------- */

/**
 * One part of the dial's strip. The Phone page draws a dial as a single row that reads left to
 * right as the call's attempts: the ones made, each with how it went; the one being made, opened
 * up into its steps; and the ones that follow if nobody answers. The steps and the run of redials
 * were two rows once, a strip and a set of tiles under it, saying the same call twice.
 */
export type StripPart =
  /** An attempt that is over: "Call 1", "Voicemail". */
  | { kind: "made"; label: string; text: string }
  /** The attempt being made, with how far it has got. `label` is null on a call that carries no
   *  redials, where the steps are the whole strip and there is no run to name a place in. */
  | { kind: "now"; label: string | null; steps: DialStep[] }
  /** An attempt still to come. `text` says when, on the first of them only: one "if no answer"
   *  reads for the row. */
  | { kind: "next"; label: string; text: string | null };

/**
 * The dial as the parts of its strip, in order. `detection` and `voicemail` are what the dial on
 * screen heard, as `dialSteps` and `attemptTiles` take them. A redial names its place in the run
 * ("Redial 1 of 2"), since the attempts to come may be drawn short; the call itself is "Call 1".
 */
export function dialStrip(
  call: DialedAttempt | null | undefined,
  stage: DialerCall["stage"] | "requested",
  detection: DialDetection | null | undefined,
  voicemail: boolean,
  label: (slug: string) => string | undefined,
): StripPart[] {
  const steps = dialSteps(stage, detection);
  const tiles = attemptTiles(call, stage, voicemail, label);
  const redial = call?.redial;
  if (!tiles || !redial) return [{ kind: "now", label: null, steps }];
  const of = Math.max(redial.of, redial.n);
  const first = tiles.findIndex((tile) => tile.state === "todo");
  return tiles.map((tile, n): StripPart => {
    if (tile.state === "done") return { kind: "made", label: tile.label, text: tile.text };
    if (tile.state === "todo") {
      return { kind: "next", label: tile.label, text: n === first ? "if no answer" : null };
    }
    return { kind: "now", label: n >= 1 ? `${tile.label} of ${of}` : tile.label, steps };
  });
}

/**
 * The voicemail note on the strip: `voicemailNote`, left out where the strip says it already.
 *
 * A dial that ended on a voicemail is usually followed at once by its redial, and the strip of a
 * redial draws the attempt before it ("Call 1, Voicemail") and says why it is made ("Call 1
 * reached a voicemail."). The note would be the same news a third time, and long enough to wrap
 * the line for the five seconds it lasts, on every voicemail of a run. It stays wherever the
 * strip does not say it: another lead is being called, nothing is, the dialer is leaving a
 * message, or the attempt before is not recorded as a voicemail yet.
 */
export function stripVoicemailNote(
  heard: HeardVoicemail | null,
  current: ({ call_id: string; lead: { id: string } } & DialedAttempt) | null,
  now: number,
): string | null {
  const note = voicemailNote(heard, current, now);
  if (note === null || heard === null || current === null) return note;
  if (current.lead.id !== heard.leadId || heard.leaving) return note;
  const n = current.redial?.n ?? 0;
  const before = n >= 1 ? current.earlier?.find((a) => a.n === n - 1) : undefined;
  return before?.outcome === "voicemail" ? null : note;
}

/* --- answering machine detection on the rep's call (ADR-104, ADR-106 in `platform`) ----------- */

/** The outcomes the voicemail suggestion sets for the rep: a message was left, or the machine
 *  was hung up on. Slugs of the tenant's catalog. */
export const VOICEMAIL_LEFT_OUTCOME = "voicemail_left";
export const VOICEMAIL_OUTCOME = "voicemail";

/** How many times the phone asks what the switch heard before it stops: the listening is over
 *  within five seconds of the answer, and this is asked once a second. */
export const DETECTION_TRIES = 12;
/** How many times it asks while core-api says the call's greeting is on its way (ADR-128 in
 *  `platform`). core-api stops saying so within about twenty seconds: this only keeps a phone from
 *  asking for ever. */
export const GREETING_TRIES = 45;

/**
 * Whether there is nothing more to wait for from the detection: it gave its verdict, nothing
 * listened to the call, the question failed (a core-api that predates the route), or it has been
 * asked often enough that the switch is no longer listening.
 */
export function detectionSettled(
  detection: { mode: string; result: string | null; greeting_expected?: boolean } | null | undefined,
  asked: number,
  failed: boolean,
): boolean {
  if (failed) return true;
  // The verdict is in, and the greeting it called for is still being made: that is what the
  // phone is waiting for now.
  if (detection?.greeting_expected) return asked >= GREETING_TRIES;
  if (detection && (detection.mode === "off" || detection.result !== null)) return true;
  return asked >= DETECTION_TRIES;
}

/**
 * Whether the phone suggests a voicemail on this call (ADR-106): a passive detection heard a
 * machine on it, the rep is connected to that lead, and they have not said it is a person.
 */
export function suggestsVoicemail(
  detection: { call_id: string; suggest_voicemail: boolean } | null | undefined,
  call: { state: string; kind: string | null; callerChannelId: string } | null,
  dismissed: ReadonlySet<string>,
): boolean {
  return (
    canDropVoicemail(call) &&
    call !== null &&
    detection?.suggest_voicemail === true &&
    detection.call_id === call.callerChannelId &&
    !dismissed.has(call.callerChannelId)
  );
}

/**
 * Whether the phone offers the cadence's voicemail on this call without anything having heard a
 * machine: the cadence set this attempt to leave a message, nothing listens to the call (the
 * campaign's detection is off), the rep is connected to that lead, and they have not said it is a
 * person. Where detection listens the phone waits for what it hears (`suggestsVoicemail`), so the
 * two are never both true.
 *
 * Without it a rep on such a campaign hears the greeting and has to know, from the cadence page,
 * that this is the attempt that leaves a message, and which one.
 */
export function offersCadenceVoicemail(
  detection: { call_id: string; mode: string; on_voicemail?: string | null } | null | undefined,
  call: { state: string; kind: string | null; callerChannelId: string } | null,
  dismissed: ReadonlySet<string>,
): boolean {
  return (
    canDropVoicemail(call) &&
    call !== null &&
    detection !== null &&
    detection !== undefined &&
    detection.call_id === call.callerChannelId &&
    detection.mode === "off" &&
    (detection.on_voicemail === "leave_message" || detection.on_voicemail === "leave_message_auto") &&
    !dismissed.has(call.callerChannelId)
  );
}

/**
 * Whether the rail's voicemail icon calls for a choice with its ring: the switch heard something,
 * the choices are folded away, and the rep did not close them with the X. A suggestion stays in
 * the rail for as long as it stands, closed or not, so that the choices can be opened again; the
 * X is the rep saying "not now", which the ring would keep arguing with for the rest of a
 * conversation with a person. A message the cadence plans, with nothing heard, never rings.
 */
export function suggestionRings(heard: boolean, open: boolean, closedByRep: boolean): boolean {
  return heard && !open && !closedByRep;
}

/** How long the choices of a voicemail suggestion stay up once they have opened by themselves. */
export const SUGGESTION_SHOWN_MS = 20_000;

/**
 * Whether the choices of a voicemail suggestion are on their way to putting themselves away.
 *
 * They open by themselves when the suggestion arrives, over whatever the rep was reading, and a
 * rep talking to a person has no hand free to close them. So they close after
 * `SUGGESTION_SHOWN_MS`, as the X would have: the icon stays in the rail, still, to open them
 * again. Only the ones that opened by themselves, though. Choices the rep opened from the icon
 * are theirs to close, and the clock does not run while the rep is at them (the pointer on them,
 * the keyboard in them, their list of other messages open), while a choice is being carried out,
 * or while an error is waiting to be read.
 */
export function suggestionTimesOut(s: {
  open: boolean;
  /** They opened because the suggestion arrived, not because the rep asked for them. */
  byItself: boolean;
  /** The rep is at them. */
  attended: boolean;
  /** Leave voicemail or Hang up is on its way. */
  working: boolean;
  /** One of them failed, and says so. */
  failed: boolean;
}): boolean {
  return s.open && s.byItself && !s.attended && !s.working && !s.failed;
}

/** "A", "A or B", "A, B or C": the outcomes a sentence names. Past three it names two and says
 *  there are more, so the line stays one a rep reads at a glance. */
function orList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length > 3) return `${names[0]}, ${names[1]} or the like`;
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/**
 * What the outcome window says when the rep's outcome sends a redial (ADR-105 in `platform`).
 *
 * A call the lead's phone answered reaches the rep, voicemail or not, where nothing hangs up on a
 * machine first. If the attempt hangs up on a voicemail and its call has a redial left, an outcome
 * that says nobody was reached sends that redial at once, for the same rep. The window says which
 * outcomes do, so the same lead ringing again is no surprise: the campaign's outcomes of the
 * no-contact kind with no wait of their own, which is the dialer's own rule. Null when no redial
 * follows, and when the campaign offers no such outcome.
 */
export function redialHint(
  detection:
    | { redial?: { n: number; of: number } | null; next_redial?: number | null }
    | null
    | undefined,
  offered: readonly { label: string; category: string | null; backoff_seconds: number | null }[],
  who: string | null,
): string | null {
  const next = detection?.next_redial ?? null;
  if (next === null) return null;
  const sends = offered
    .filter((o) => o.category === "no_contact" && o.backoff_seconds === null)
    .map((o) => o.label);
  if (sends.length === 0) return null;
  const of = Math.max(detection?.redial?.of ?? next, next);
  const stay = who ? ` You stay on ${who}.` : "";
  return `Pick ${orList(sends)} and redial ${next} of ${of} goes out right away.${stay}`;
}

/** The message "Leave voicemail" plays: the lead's own (ADR-061), else the library's first. */
export function messageToLeave<T extends { slug: string }>(
  messages: readonly T[],
  suggested: string | null,
): T | null {
  return voicemailOrder(messages, suggested)[0] ?? null;
}

/**
 * What the suggestion offers on this attempt, by the cadence (ADR-107 in `platform`).
 *
 * The cadence says, call by call and redial by redial, what happens when a voicemail answers:
 * hang up, after which a redial may follow at once, or leave a message, and which one. The
 * suggestion makes that the main button and says it in a sentence. The other choice stays beside
 * it, because the rep heard the call and the cadence did not. A dial the cadence says nothing
 * about (a campaign with no cadence, a core-api that does not send it) keeps Leave voicemail as
 * the main one.
 */
export interface SuggestionPlan<T> {
  /** The cadence's choice for this attempt, or null when it says nothing. */
  cadence: "hang_up" | "leave_message" | null;
  /** Which of the two buttons is the main one. */
  main: "leave" | "hangup";
  /** The message Leave voicemail plays: the one the cadence names for this attempt while the
   *  library still has it, else the lead's own, else the library's first. */
  message: T | null;
  /** The rest of the library, one click away. */
  others: T[];
  /** The redial that follows a hang-up at once, and of how many. */
  redial: { next: number; of: number } | null;
  /** The message core-api prepared for this call (ADR-128 in `platform`), when it named one. It
   *  comes before `message`: core-api picked it with greetings written as text in the running,
   *  and the phone's own list holds recordings only. A greeting that failed is here too, so the
   *  phone can say why; `greetingToLeave` says whether it can be left. */
  greeting: PreparedGreeting | null;
}

/** What the phone needs of a prepared greeting. `CallGreeting` in `lib/types.ts` is one. */
export interface PreparedGreeting {
  slug: string;
  name: string;
  kind: string;
  status: string;
  words: string | null;
  /** Which of a greeting's two texts it is (ADR-116 in `platform`); null when it has one. */
  version?: string | null;
  voice: string | null;
  /** True when the voice is the rep's own (ADR-117 in `platform`): `voice` is then the rep's name. */
  voice_own?: boolean;
  problem: string | null;
}

/**
 * Which version of a greeting a call was given, for the rep to read, or null for a greeting with
 * one text and for a recording: `label` names it, and `why` says what made it that one. Version B
 * is the one for a lead nobody has reached for a day; core-api decides, and the phone only says.
 */
export function greetingVersion(
  greeting: Partial<PreparedGreeting> | null,
): { label: string; why: string; second: boolean } | null {
  if (greeting?.version === "b") {
    return { label: "Version B", why: "Nobody has reached this lead for a day", second: true };
  }
  if (greeting?.version === "a") {
    return { label: "Version A", why: "This lead was first called less than a day ago, or was reached since", second: false };
  }
  return null;
}

/** The prepared greeting Leave voicemail plays, or null when there is none or it failed. One
 *  that is still being made counts: the click waits for it. */
export function greetingToLeave(plan: { greeting: PreparedGreeting | null }): PreparedGreeting | null {
  return plan.greeting !== null && plan.greeting.status !== "failed" ? plan.greeting : null;
}

/** What Leave voicemail plays on this call: the prepared greeting, else the plan's recording. */
export function leaves<T extends { slug: string; name: string }>(
  plan: SuggestionPlan<T>,
): { slug: string; name: string } | null {
  return greetingToLeave(plan) ?? plan.message;
}

/**
 * How a prepared greeting stands, in a line for the rep, or null when there is nothing to say: a
 * recording that is ready needs no line.
 */
export function greetingLine(greeting: PreparedGreeting | null): string | null {
  if (greeting === null) return null;
  if (greeting.status === "failed") {
    return `${greeting.problem ?? "The greeting could not be made."} Say it yourself, or leave a recording.`;
  }
  if (greeting.kind !== "script") return null;
  // The suggestion is on the phone of the rep the greeting is for, so their own voice is "your".
  const by = greeting.voice_own ? " in your own voice" : greeting.voice ? ` in ${greeting.voice}'s voice` : "";
  return greeting.status === "making"
    ? `Getting it ready${by}. You can press Leave voicemail now: it plays as soon as it is ready.`
    : `Ready${by}.`;
}

export function suggestionPlan<T extends { slug: string }>(
  detection:
    | {
        on_voicemail?: string | null;
        voicemail_message?: string | null;
        redial?: { n: number; of: number } | null;
        next_redial?: number | null;
        greeting?: PreparedGreeting | null;
      }
    | null
    | undefined,
  messages: readonly T[],
  suggested: string | null,
): SuggestionPlan<T> {
  // A message the dialer would leave by itself is the rep's to leave where the rep has the call.
  const says = detection?.on_voicemail === "leave_message_auto" ? "leave_message" : detection?.on_voicemail;
  const cadence = says === "hang_up" || says === "leave_message" ? says : null;
  const named = detection?.voicemail_message ?? null;
  const lead = named !== null && messages.some((m) => m.slug === named) ? named : suggested;
  const next = cadence === "hang_up" ? (detection?.next_redial ?? null) : null;
  const greeting = detection?.greeting ?? null;
  const ordered = voicemailOrder(messages, lead);
  // With a greeting to leave, every recording is one of the others: the greeting is the first.
  // A recording core-api picked is in the list already, and is not offered twice.
  const first = greeting !== null && greeting.status !== "failed";
  return {
    cadence,
    main: cadence === "hang_up" ? "hangup" : "leave",
    message: messageToLeave(messages, lead),
    others: first ? ordered.filter((m) => m.slug !== greeting.slug) : ordered.slice(1),
    redial: next === null ? null : { next, of: Math.max(detection?.redial?.of ?? next, next) },
    greeting,
  };
}

/** What the cadence does on a voicemail at this attempt, in a sentence; null when it says
 *  nothing. */
export function cadenceSays(
  plan: Omit<SuggestionPlan<{ name: string }>, "greeting"> & { greeting?: PreparedGreeting | null },
): string | null {
  if (plan.cadence === "hang_up") {
    return plan.redial
      ? `The cadence hangs up here and calls again at once: redial ${plan.redial.next} of ${plan.redial.of}.`
      : "The cadence hangs up here.";
  }
  if (plan.cadence === "leave_message") {
    const prepared = plan.greeting && plan.greeting.status !== "failed" ? plan.greeting : null;
    const message = prepared ?? plan.message;
    return message ? `The cadence leaves “${message.name}” here.` : "The cadence leaves a message here.";
  }
  return null;
}

/** An outcome the suggestion chose for a call, kept until the call's outcome window asks. */
export interface PlannedOutcome {
  /** The lead's channel: what the queue session and the disposition name. */
  callId: string;
  slug: string;
}

/**
 * The outcome to record without asking, for the call whose window has just opened: the one the
 * suggestion planned for this very call, if the campaign offers it and it needs nothing more from
 * the rep. Anything else (another call, an outcome the campaign does not offer, one that wants a
 * note or a time) is null, and the window asks as it always does.
 */
export function plannedOutcomeFor<
  T extends { slug: string; kind: string; requires_note: boolean },
>(
  planned: PlannedOutcome | null,
  session: { channel_uuid: string } | null,
  offered: readonly T[],
): T | null {
  if (planned === null || session === null || planned.callId !== session.channel_uuid) return null;
  const outcome = offered.find((o) => o.slug === planned.slug);
  if (!outcome || outcome.requires_note || outcome.kind === "callback") return null;
  return outcome;
}
